import * as CL from '@design-system/component-library';
import { type ChangeEvent, useEffect, useState } from 'react';
import { dsClass } from '../../../../common/constants/dsClasses.js';
import {
  additionalServicesMsg,
  deductibleMsg,
  deviceEnrollmentProgramAliasInfoMsg,
  enrollmentAliasPlaceHolderMsg,
  optionalMsg,
  squareTradeDeductibleMsg,
  t,
} from '../../../../common/i18n/index.js';
import { isSquareTradeAddOn } from '../../../../common/utils/addOnUtils.js';
import { formatSum } from '../../../../common/utils/priceUtils.js';
import {
  type EnrollmentProgramType,
  EnrollmentProgramType as EnrollmentProgramTypeEnum,
} from '../../../../generated/api/enrollmentProgramType.js';
import { getAddOnsAndDefaultValues, valueOrZero } from '../../../ProductDetails/utils/productDetailsUtils.js';
import { getCommercialProductForGuid } from '../../../ShoppingBasket/shoppingBasketUtils.js';
import { ToggleCheckbox } from '../../../ToggleCheckbox/ToggleCheckbox.js';
import { getEnrollmentProgramTypeFromOffer } from '../../utils/placeOrderUtils.js';
import type { BasketItem } from '../../../../common/types/shoppingBasket.js';
import type { AddOn } from '../../../../generated/api/addOn.js';
import type { ChildOrderItem } from '../../../../generated/api/childOrderItem';
import type { CompanyInfoResponse } from '../../../../generated/api/companyInfoResponse';
import type { Offer } from '../../../../generated/api/offer.js';
import type { OnlineModel } from '../../../../generated/api/onlineModel.js';
import type { AuthenticatedUser } from '../../../../public/site/authState.js';
import type { ProductDetailsAddOnStateProperties } from '../../../ProductDetails/partials/ProductDetailsAddOn.js';
import type { PlaceOrderItemUnit } from '../../placeOrderTypes';

export interface DeviceAddOnTogglePayload extends ChildOrderItem {
  isChecked: boolean;
}

export interface EnrollmentProgramTogglePayload {
  programType: EnrollmentProgramType;
  isSelected: boolean;
  alias?: string;
}

export interface DeviceAddonsViewProps {
  basketItem: BasketItem;
  onlineModel: OnlineModel;
  offer: Offer;
  user: AuthenticatedUser;
  companyInfo: CompanyInfoResponse;
  onAddOnToggle?: (payload: DeviceAddOnTogglePayload) => void;
  onEnrollmentProgramToggle?: (payload: EnrollmentProgramTogglePayload) => void;
  orderItem: PlaceOrderItemUnit;
}

const recognizedProgramTypes: EnrollmentProgramType[] = [
  EnrollmentProgramTypeEnum.DEP,
  EnrollmentProgramTypeEnum.KME,
  EnrollmentProgramTypeEnum.ZEROTOUCH,
  EnrollmentProgramTypeEnum.AUTOPILOT,
];

type ToggleHandler = (checked: boolean) => void;

const AddOnDescription = ({ addOn }: { addOn: AddOn }) =>
  isSquareTradeAddOn(addOn) ? (
    <div className="of-product-details-addon-description">
      <p>
        {t.WW2M(
          'Secure your device for accident - on accidental incident you will get new equivalent replacement device with one phone call.'
        )}
        <a
          target="_blank"
          href="https://elisa.fi/asiakaspalvelu/aihe/matkapuhelinliittymat/ohje/square-trade/"
          rel="noreferrer"
        >{` ${t.BQ9Y('More details')}.`}</a>
      </p>
    </div>
  ) : null;

const getPriceInfo = (addOn: AddOn) => {
  const monthly = valueOrZero(addOn.addOnMonthlyRecurringCharge);
  const oneTime = valueOrZero(addOn.addOnOneTimeCharge);

  if (monthly) {
    return {
      value: (
        <span>
          {formatSum(monthly, true)}€/{t.XXVX('month')}
        </span>
      ),
      additionalText: isSquareTradeAddOn(addOn) ? (
        <div
          className={dsClass.TEXT_XS}
        >{`${t.VWSV(deductibleMsg)} ${squareTradeDeductibleMsg}. ${t.XJMC('No fixed term.')}`}</div>
      ) : undefined,
    };
  }

  if (oneTime) {
    return {
      value: <span>{formatSum(oneTime, true)} €</span>,
    };
  }

  return undefined;
};

const AddOnItem = ({ state, onToggle }: { state: ProductDetailsAddOnStateProperties; onToggle: ToggleHandler }) => (
  <ToggleCheckbox
    key={state.addOn.addOnCode}
    disabled={state.isDisabled}
    initialValue={state.isChecked}
    onChange={() => onToggle(!state.isChecked)}
    title={state.addOn.addOnProductName}
    priceInfo={getPriceInfo(state.addOn)}
  >
    <AddOnDescription addOn={state.addOn} />
  </ToggleCheckbox>
);

const useAddOnState = (
  basketItem: BasketItem,
  onlineModel: OnlineModel,
  offer: Offer,
  user: AuthenticatedUser,
  companyInfo: CompanyInfoResponse,
  orderItem: PlaceOrderItemUnit
) => {
  const commercialProduct = getCommercialProductForGuid(basketItem.offer.commercialProductGuid, onlineModel);

  const getDefaults = (): ProductDetailsAddOnStateProperties[] => {
    if (!commercialProduct) {
      return [];
    }

    const addOnsWithDefaults = getAddOnsAndDefaultValues(
      onlineModel.addOns || [],
      commercialProduct,
      companyInfo.eppSolution?.damageInsuranceDeviceCategories,
      false,
      basketItem.quantity,
      offer.eppCategory,
      user
    );

    // Initialize checked state from orderItem.addons
    return addOnsWithDefaults.map(addon => ({
      ...addon,
      isChecked: orderItem.addons?.some(orderAddon => orderAddon.addOnCode === addon.addOn.addOnCode),
    }));
  };

  const [addOnsState, setAddOnsState] = useState<ProductDetailsAddOnStateProperties[]>(getDefaults);

  useEffect(() => {
    setAddOnsState(getDefaults());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    commercialProduct,
    onlineModel.addOns,
    companyInfo.eppSolution?.damageInsuranceDeviceCategories,
    basketItem.quantity,
    offer.eppCategory,
    user,
    orderItem.addons,
  ]);

  return { addOnsState, setAddOnsState, commercialProduct };
};

export const getApplicableEnrollmentProgram = (
  offer: Offer,
  companyInfo: CompanyInfoResponse
): EnrollmentProgramType | undefined => {
  const program = getEnrollmentProgramTypeFromOffer(offer);
  if (!program || !recognizedProgramTypes.includes(program)) {
    return undefined;
  }

  const hasCompanyProgram = companyInfo.enrollmentPrograms?.some(
    enrollment => enrollment.enrollmentProgramSystem === program
  );

  return hasCompanyProgram ? program : undefined;
};

export interface UseHasDeviceAddonsParams {
  basketItem: BasketItem;
  onlineModel: OnlineModel;
  offer: Offer;
  user: AuthenticatedUser;
  companyInfo: CompanyInfoResponse;
}

export const useHasDeviceAddons = ({
  basketItem,
  onlineModel,
  offer,
  user,
  companyInfo,
}: UseHasDeviceAddonsParams): boolean => {
  const commercialProduct = getCommercialProductForGuid(basketItem.offer.commercialProductGuid, onlineModel);
  const applicableProgram = getApplicableEnrollmentProgram(offer, companyInfo);

  if (!commercialProduct) {
    return !!applicableProgram;
  }

  const addOnsWithDefaults = getAddOnsAndDefaultValues(
    onlineModel.addOns || [],
    commercialProduct,
    companyInfo.eppSolution?.damageInsuranceDeviceCategories,
    false,
    basketItem.quantity,
    offer.eppCategory,
    user
  );

  return addOnsWithDefaults.length > 0 || !!applicableProgram;
};

const useEnrollmentProgramState = (offer: Offer, companyInfo: CompanyInfoResponse, orderItem: PlaceOrderItemUnit) => {
  const getApplicableProgram = (): EnrollmentProgramType | undefined =>
    getApplicableEnrollmentProgram(offer, companyInfo);

  const applicableProgram = getApplicableProgram();

  // Initialize from orderItem.enrollmentProgram and orderItem.enrollmentProgramAlias
  const [isSelected, setSelected] = useState(
    !!orderItem.enrollmentProgram && orderItem.enrollmentProgram === applicableProgram
  );
  const [alias, setAlias] = useState(orderItem.enrollmentProgramAlias ?? '');

  useEffect(() => {
    const shouldBeSelected = !!orderItem.enrollmentProgram && orderItem.enrollmentProgram === applicableProgram;
    setSelected(shouldBeSelected);
    setAlias(orderItem.enrollmentProgramAlias ?? '');
  }, [applicableProgram, orderItem.enrollmentProgram, orderItem.enrollmentProgramAlias]);

  return {
    applicableProgram,
    isSelected,
    alias,
    setSelected,
    setAlias,
    showAlias: applicableProgram === EnrollmentProgramTypeEnum.KME,
  };
};

interface EnrollmentProgramRowProps {
  program: EnrollmentProgramType;
  isSelected: boolean;
  onToggle: (checked: boolean) => void;
  alias: string;
  onAliasChange: (event: string | ChangeEvent<HTMLInputElement>) => void;
  showAlias: boolean;
}

const getEnrollmentProgramLabel = (program?: EnrollmentProgramType) => {
  switch (program) {
    case EnrollmentProgramTypeEnum.DEP:
      return t.CZS5('Apple device enrollment program');
    case EnrollmentProgramTypeEnum.KME:
      return t.ZRXP('Samsung KME enrollment program');
    case EnrollmentProgramTypeEnum.ZEROTOUCH:
      return t.ZO3A('Android Zero-touch (ZT) enrollment program');
    case EnrollmentProgramTypeEnum.AUTOPILOT:
      return t.G9OG('Windows Autopilot enrollment program');
    default:
      return t.Q03S('Device enrollment program');
  }
};

const EnrollmentProgramRow = ({
  program,
  isSelected,
  onToggle,
  alias,
  onAliasChange,
  showAlias,
}: EnrollmentProgramRowProps) => (
  <div className={dsClass.MARGIN_VERTICAL_2}>
    <ToggleCheckbox
      key={`enrollment-${program}`}
      initialValue={isSelected}
      onChange={() => onToggle(!isSelected)}
      title={getEnrollmentProgramLabel(program)}
    >
      <>
        <p className={dsClass.MARGIN_BOTTOM_2}>
          {t.Z20L('Device enrollment programs ensure managed provisioning for your company devices.')}
        </p>
        {showAlias && isSelected && (
          <div className={dsClass.MARGIN_TOP_2}>
            <label className={dsClass.INPUT_LABELAREA_LABEL}>{t.TUZU(`Customer's Device Enrollment Alias`)}</label>
            <div className={dsClass.DISPLAY_FLEX}>
              <CL.Input
                name="enrollmentAlias"
                type="text"
                value={alias}
                placeholder={t.IBVP(enrollmentAliasPlaceHolderMsg)}
                onValueChange={onAliasChange}
                maxlength={64}
                optional={true}
                controlled={true}
                i18n_input_optionalText={t.G7RQ(optionalMsg)}
              />
              <CL.Tooltip
                triggerElement={<CL.Icon className={`${dsClass.MARGIN_LEFT_2}`} icon="information" size="s" />}
                i18n_tooltip_contentText={t.BJRZ(deviceEnrollmentProgramAliasInfoMsg)}
                placement="top"
              />
            </div>
          </div>
        )}
      </>
    </ToggleCheckbox>
  </div>
);

export const DeviceAddonsView = ({
  basketItem,
  onlineModel,
  offer,
  user,
  companyInfo,
  onAddOnToggle,
  onEnrollmentProgramToggle,
  orderItem,
}: DeviceAddonsViewProps) => {
  const { addOnsState, setAddOnsState, commercialProduct } = useAddOnState(
    basketItem,
    onlineModel,
    offer,
    user,
    companyInfo,
    orderItem
  );
  const { applicableProgram, isSelected, alias, setSelected, setAlias, showAlias } = useEnrollmentProgramState(
    offer,
    companyInfo,
    orderItem
  );

  if (!commercialProduct && !applicableProgram) {
    return null;
  }

  const handleAddOnToggle = (target: ProductDetailsAddOnStateProperties, nextChecked: boolean) => {
    setAddOnsState(prev =>
      prev.map(item => (item.addOn.addOnCode === target.addOn.addOnCode ? { ...item, isChecked: nextChecked } : item))
    );

    onAddOnToggle?.({
      addOnCode: target.addOn.addOnCode,
      addOnAssociationCode: target.addOnAssociationCode,
      isChecked: nextChecked,
    });
  };

  const handleEnrollmentToggle = (checked: boolean) => {
    setSelected(checked);
    if (!applicableProgram) {
      return;
    }

    onEnrollmentProgramToggle?.({
      programType: applicableProgram,
      isSelected: checked,
      alias: alias || undefined,
    });
  };

  const handleAliasChange = (event: string | ChangeEvent<HTMLInputElement>) => {
    const value = typeof event === 'string' ? event : event.currentTarget.value;
    setAlias(value);

    // Propagate alias change to parent
    if (applicableProgram && isSelected) {
      onEnrollmentProgramToggle?.({
        programType: applicableProgram,
        isSelected: true,
        alias: value || undefined,
      });
    }
  };

  if (addOnsState.length === 0 && !applicableProgram) {
    return null;
  }

  return (
    <div className="of-device-addons-view">
      <h5 className={dsClass.MARGIN_BOTTOM_3}>{t.LXSR(additionalServicesMsg)}</h5>
      <div className="of-device-addons-view__list">
        {addOnsState.map(addOnState => (
          <AddOnItem
            key={addOnState.addOn.addOnCode}
            state={addOnState}
            onToggle={checked => handleAddOnToggle(addOnState, checked)}
          />
        ))}
        {applicableProgram && (
          <EnrollmentProgramRow
            program={applicableProgram}
            isSelected={isSelected}
            onToggle={handleEnrollmentToggle}
            alias={alias}
            onAliasChange={handleAliasChange}
            showAlias={showAlias}
          />
        )}
      </div>
    </div>
  );
};
